

























































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import {
  UpdateProjectGeneralSettingRequestPayload,
  ProjectDetailPayload,
  ProjectStatus,
} from '@/store/modules/projects/types/projects.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { isEqual as _isEqual } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import BaseDialog from '../../../components/base/BaseDialog.vue';
import ArchiveModalBox from '../../../components/ArchiveModalBox.vue';
import DeleteModalBox from '../../../components/DeleteModalBox.vue';
import { useUserEmail } from '@/utils/user.util';
import SiteSettings from '@/views/Project/components/SiteSettings.vue';
import DomainCategorySettings from '../../Grip/DomainCategory/DomainCategorySettings.vue';
import BarrierSettings from '../../Grip/Barrier/BarrierSettings.vue';
import EnablerSettings from '../../Grip/Enabler/EnablerSettings.vue';
import StrategySettings from '../../Grip/Strategy/StrategySettings.vue';
import { cloneData } from '@/utils/general.util';

@Component({
  components: {
    BaseDialog,
    SiteSettings,
    DomainCategorySettings,
    BarrierSettings,
    EnablerSettings,
    StrategySettings,
  },
})
export default class ProjectSettingsTab extends Vue {
  @Prop() public projectId!: number;
  @Prop() public projectDetail!: ProjectDetailPayload;
  @Prop() public projectSettingInfo!: any;
  @Prop() public isProjectOwner!: boolean;
  @Prop({ default: 0 }) public activeTab!: number;

  public settingTab: number = 0;
  public isGeneralSettingSubmitButtonDisabled: boolean = true;
  public projectSettingDetail: any = [];
  public projectDetailInfo: any = [];
  public tabVisiblity: boolean = false;
  public gripTabDisabled: boolean = false;
  public showBarrierTab: boolean = false;
  public showEnablerTab: boolean = false;
  public showStrategyTab: boolean = false;
  public showDomainCategoryTab: boolean = false;
  public projectStatus: ProjectStatus = this.projectDetail.status
    ? (this.projectDetail.status as ProjectStatus)
    : ProjectStatus.ACTIVE;
  public updateProjectStatusTo: ProjectStatus = this.projectStatus;

  /** Adding unique keys for tab components for dynamically adding or removing tab components */
  public generalSettingsTabKey: number = Math.floor(Math.random() * 999);
  public siteTabKey: number = Math.floor(Math.random() * 999);
  public barrierTabKey: number = Math.floor(Math.random() * 999);
  public enablerTabKey: number = Math.floor(Math.random() * 999);
  public strategyTabKey: number = Math.floor(Math.random() * 999);
  public domainCategoryTabKey: number = Math.floor(Math.random() * 999);

  @Action('projects/getProjectDetail')
  public getProjectDetail!: (payload: { projectId: number }) => void;

  @Action('projects/publishProjectById')
  public publishProjectById!: (projectId: number) => void;

  @Action('projects/archiveProjectById')
  public archiveProjectById!: (projectId: number) => void;

  @Action('projects/deleteProjectById')
  public deleteProjectById!: (projectId: number) => void;

  @Action('projects/updateProjectGeneralSettings')
  public updateProjectGeneralSettings!: (
    params: UpdateProjectGeneralSettingRequestPayload,
  ) => void;

  @State((state) => state.projects.updatedProjectGeneralSettings)
  public updatedProjectGeneralSettings!: any;

  @State((state) => state.projects.apiState.updateProjectStatus.success)
  public updateProjectSuccess!: boolean;

  @State((state) => state.projects.projectName)
  public projectNameState!: any;

  @State((state) => state.projects.auditGripType)
  public auditGripType!: any;

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  get isProjectArchived(): boolean {
    return this.projectStatus === ProjectStatus.ARCHIVED;
  }

  get editable(): boolean {
    return !this.isProjectArchived;
  }

  /**
   * Handles the logic required when a tab changes, such as resetting visibility flags, disabling / enabling the GRIP tab, and updating component keys to force re-rendering.
   * This function will be passed as the callback to useTabChangeGuard.
   *
   * @param {number} tabIndex - The index of the tab being switched to.
   */
  private handleTabContentLoading(tabIndex: number): void {
    // Reset visibility for GRIP sub-tabs initially
    this.showBarrierTab = false;
    this.showEnablerTab = false;
    this.showStrategyTab = false;
    this.showDomainCategoryTab = false;

    // Determine actual indices for roles
    const ownerGripIndex = 2;
    const ownerBarrierIndex = 3;
    const coordinatorGripIndex = 1;
    const coordinatorBarrierIndex = 2;

    // Handle GRIP tab redirection first
    if (this.isProjectOwner && tabIndex === ownerGripIndex) {
      this.gripTabDisabled = true; // Disable GRIP tab
      this.$nextTick(() => {
        if (this.settingTab === ownerGripIndex) {
          // Ensure we are still on the GRIP tab before changing
          this.settingTab = ownerBarrierIndex; // Change to Barrier tab index for Owner
        }
      });
      return; // Exit early, the guard will re-trigger for the new settingTab value
    }

    if (!this.isProjectOwner && tabIndex === coordinatorGripIndex) {
      this.gripTabDisabled = true; // Disable GRIP tab
      this.$nextTick(() => {
        if (this.settingTab === coordinatorGripIndex) {
          // Ensure we are still on the GRIP tab before changing
          this.settingTab = coordinatorBarrierIndex; // Change to Barrier tab index for Coordinator
        }
      });
      return; // Exit early, the guard will re-trigger for the new settingTab value
    }

    // Determine visibility and update keys based on the new tab index
    switch (tabIndex) {
      case 0: // General
        this.tabVisiblity = false;
        this.gripTabDisabled = false;
        this.generalSettingsTabKey += 1;
        break;
      case 1: // Owner: Sites / Coordinator: GRIP
        if (!this.isProjectOwner) {
          // Owner on Sites tab (their index 1)
          this.tabVisiblity = false;
          this.gripTabDisabled = false;
          this.siteTabKey += 1;
        }
        break;

      case 2: // Owner: GRIP (handled by redirection) / Coordinator: Barrier
        this.gripTabDisabled = true;
        if (!this.isProjectOwner) {
          this.tabVisiblity = true;
          this.showBarrierTab = true;
          this.gripTabDisabled = true;
          this.barrierTabKey += 1;
        }
        break;
      case 3: // Owner: Barrier / Coordinator: Enabler
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          // Coordinator: Strategy
          this.showStrategyTab = true;
          this.enablerTabKey += 1;
        } else {
          // Owner on Barrier Tab
          this.showBarrierTab = true;
          this.barrierTabKey += 1;
        }
        break;
      case 4: // Owner: Enabler / Coordinator: Strategy
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          this.showStrategyTab = true;
          this.strategyTabKey += 1;
        } else {
          // Project Owner: Enabler
          this.showEnablerTab = true;
          this.enablerTabKey += 1;
        }
        break;
      case 5: // Strategy (Owner only)
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          // Coordinator on Domain/Category tab (their index 5)
          this.showDomainCategoryTab = true;
          this.domainCategoryTabKey += 1;
        } else {
          // Owner on Strategy tab (their index 5)
          this.showStrategyTab = true;
          this.strategyTabKey += 1;
        }
        break;
      case 6: // Owner: Domain/Category
        if (this.isProjectOwner) {
          // Owner on Domain/Category tab (their index 6)
          this.gripTabDisabled = true;
          this.tabVisiblity = true;
          this.showDomainCategoryTab = true;
          this.domainCategoryTabKey += 1;
        }
        break;

      default:
        this.tabVisiblity = false;
        this.gripTabDisabled = false;
    }
  }

  public updateDisableButton(type: string) {
    if (type === 'projectDescription') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.description,
        this.projectSettingDetail.description,
      );
    } else if (type === 'enableProjectGrip') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.enableProjectGrip,
        this.projectSettingDetail.enableProjectGrip,
      );
    } else {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.name,
        this.projectSettingDetail.name,
      );
    }
  }

  public handleUnarchiveProject() {
    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: 'Unarchive Project?',
        content: `This will enable the project and its data collection period to be edited. You may archive the project again anytime`,
        confirmButtonText: 'Cancel',
        cancelButtonText: 'Unarchive',
      },
      events: {
        cancel: () => {
          this.updateProjectStatusTo = ProjectStatus.ACTIVE;
          this.publishProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public handleArchiveProject() {
    this.$buefy.modal.open({
      parent: this,
      component: ArchiveModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: `Archive this Project?`,
        modalContent: `Archiving '${this.projectSettingDetail.name}' will convert the project to view-only and disable any ongoing data collection period under this project.`,
      },
      events: {
        confirm: () => {
          this.updateProjectStatusTo = ProjectStatus.ARCHIVED;
          this.archiveProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public handleDeleteProject() {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Project?',
        modalContent: `Deleting the project '${this.projectSettingDetail.name}' will remove it completely from PACES and you can no longer access any data collection period made under this project.`,
      },
      events: {
        confirm: () => {
          this.updateProjectStatusTo = ProjectStatus.DELETED;
          this.deleteProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public updateGeneralSettingHandler() {
    this.updateProjectGeneralSettings({
      projectId: this.projectId,
      projectName: this.projectSettingDetail.name.trim(),
      projectDescription: this.projectSettingDetail.description
        ? this.projectSettingDetail.description.trim()
        : '',
      enableProjectGrip: this.projectSettingDetail.enableProjectGrip,
    });
  }

  @Watch('settingTab')
  /**
   * Emit an event when the internal setting tab index changes.
   * @param {number} newIndex The new index of the active internal setting tab.
   * @param {number} oldIndex The old index of the active internal setting tab.
   */
  onInternalSettingTabChange(newIndex: number, oldIndex: number) {
    if (newIndex !== oldIndex) {
      this.handleTabContentLoading(newIndex);
      this.$emit('active-vertical-tab-index-changed', newIndex);
    }
  }

  public mounted(): void {
    this.projectSettingDetail = cloneData(this.projectSettingInfo);
    this.projectDetailInfo = cloneData(this.projectDetail);
    this.settingTab = this.activeTab;

    this.handleTabContentLoading(this.settingTab);
  }

  @Watch('updatedProjectGeneralSettings')
  @isTruthy
  public updateSavedGeneralSettings(newValue: any) {
    this.isGeneralSettingSubmitButtonDisabled = true;
    const updatedProjectGeneralData = {
      title: newValue.title,
      description: newValue.description,
      enableProjectGrip: newValue.enableProjectGrip,
    };
    this.$emit('updateProjectSettings', updatedProjectGeneralData);
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
    });
  }

  @Watch('updateProjectSuccess')
  @isTruthy
  public watchUpdatedStatus(): void {
    this.projectStatus = this.updateProjectStatusTo as ProjectStatus;
    this.$emit('updatedProjectStatus', this.projectStatus);
    if (this.projectStatus === ProjectStatus.DELETED) {
      this.$router.push({
        name: 'project-me',
      });
    }
  }
}
